import React, { useCallback, useMemo, useState } from 'react';

import { PAGE_SIZES, TableRowGrid } from 'components/table/models';
import Pagination from 'components/table/Pagination';
import { TableContainer } from 'components/table/styledElements/tableStyledElements';
import TableHeader from 'components/table/TableHeader';
import TableInfo from 'components/table/TableInfo';
import TableRow from 'components/table/tableRow/TableRow';
import calculateMaxPagesNumber from 'helpers/calculateMaxPagesNumber';
import { cleanAccountLocalStorage } from 'helpers/connectToLocalStorage';

import { CorporateAccount } from '../../accountsServices/CorporateAccountsService';
import CorporateAccountContextProvider from '../../context/CorporateAccountContext';
import { ACCOUNTS_SORT_BY_FE } from '../../models';

import CorporateDetailsModal from './CorporateDetailsModal';
import EditCorporateModal from './EditCorporateModal';

type Props = {
  data?: CorporateAccount[];
  dataAmount: number;
  pageSize: number;
  page: number;
  sortByListItemIndex: number;
  sortAccounts: (index: number) => void;
  setPage: (index: number) => void;
  setPageSize: (pageSize: number) => void;
};

const CorporateTable = ({
  data,
  pageSize,
  sortAccounts,
  setPage,
  page,
  setPageSize,
  dataAmount,
  sortByListItemIndex,
}: Props) => {
  const [dataForModal, setDataForModal] = useState<CorporateAccount | null>(
    data?.[0] || null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [accountForEditing, setAccountForEditing] =
    useState<CorporateAccount>();

  const maxPages = useMemo(
    () => calculateMaxPagesNumber(dataAmount, pageSize),
    [dataAmount, pageSize]
  );

  const closeModalWindow = useCallback(() => setIsModalOpen(false), []);

  const toggleEditingModalWindow = useCallback(() => {
    setIsEditingModalOpen(!isEditingModalOpen);
    cleanAccountLocalStorage();
  }, [isEditingModalOpen]);

  const openModalWindow = (data: CorporateAccount) => {
    setIsModalOpen(true);
    setDataForModal(data);
  };

  return (
    <>
      <TableInfo
        pageSizes={PAGE_SIZES}
        dataAmount={dataAmount}
        activePageSize={pageSize}
        changePageSize={setPageSize}
      />
      <TableContainer>
        <TableHeader
          parametersForSorting={ACCOUNTS_SORT_BY_FE}
          sortByListItemIndex={sortByListItemIndex}
          sortData={sortAccounts}
          tableName={TableRowGrid.Account}
        />
        {data && (
          <div>
            {data.map((item) => (
              <TableRow
                key={item.id}
                tableName={TableRowGrid.Account}
                data={[item.companyName, item.status]}
                openModalWindow={() => openModalWindow(item)}
              />
            ))}
          </div>
        )}
        {dataForModal && (
          <CorporateDetailsModal
            account={dataForModal}
            isOpen={isModalOpen}
            setDataForEditing={setAccountForEditing}
            closeWindow={closeModalWindow}
            openEditingModalWindow={() => {
              toggleEditingModalWindow();
              closeModalWindow();
            }}
          />
        )}
      </TableContainer>
      <Pagination
        dataAmount={dataAmount}
        currentPage={page}
        maxPages={maxPages}
        changeActivePage={setPage}
      />
      {isEditingModalOpen && accountForEditing && (
        <CorporateAccountContextProvider>
          <EditCorporateModal
            accountForEditing={accountForEditing}
            closeModalWindow={toggleEditingModalWindow}
          />
        </CorporateAccountContextProvider>
      )}
    </>
  );
};

export default CorporateTable;
